<template>
  <div class="bg-white brand-input">
    <div v-if="loadState === LOAD_STATE.LOADED">
      <!-- Non-empty state -->
      <div>
        <header
          class="sticky top-0 z-40 lg:flex lg:items-center lg:justify-between bg-brand-sand py-2 lg:pt-3 lg:pb-2 border-b border-brand-dark-sand"
        >
          <div>
            <router-link
              class="text-center lg:text-left lg:flex-grow-0 justify-left lg:w-96 flex-grow-1 flex items-center pl-4 cursor-pointer"
              to="/dunning/exclusions"
            >
              <span class="ml-2 mr-4"
                ><font-awesome-icon :icon="['fas', 'arrow-left']" class="text-brand-brown font-semibold"></font-awesome-icon
              ></span>
              <span class="text-sm text-brand-black font-bold overflow-ellipsis overflow-hidden whitespace-nowrap">
                {{ exclusion && exclusion.name ? `"${exclusion.name}" Exclusion` : 'New Exclusion' }}
              </span>
            </router-link>
          </div>
          <div class="mx-auto flex flex-grow max-w-screen-sm" />
          <div
            v-if="isNew"
            class="flex flex-col items-center flex-grow-0 mt-2 mx-auto lg:mx-3 lg:mt-0 lg:flex-shrink-0 lg:space-x-3 lg:flex-row lg:w-96 lg:mr-4 lg:justify-end"
          >
            <div class="mx-auto my-2 lg:inline-block lg:mx-0 lg:my-0 space-x-3">
              <!-- Create exclusion button -->
              <el-button
                type="primary"
                @click="create"
                v-loading="createLoading"
                :disabled="saveState === SAVE_STATE.SAVING || !isValid"
                id="flow-create"
              >
                <span class="font-bold"> Create Exclusion → </span>
              </el-button>
            </div>
          </div>
          <div
            v-else
            class="flex flex-col items-center flex-grow-0 mt-2 mx-auto lg:mx-3 lg:mt-0 lg:flex-shrink-0 lg:space-x-3 lg:flex-row lg:w-96 lg:mr-4 lg:justify-end"
          >
            <!-- Exclusion save state -->
            <save-state-tag :status="saveState" />
            <div class="mx-auto my-2 lg:inline-block lg:my-0 space-x-3">
              <!-- Publish campaign blueprint button -->
              <div
                v-if="!hasUnpublishedChanges && saveState === SAVE_STATE.SAVED"
                class="bg-brand-medium-blue text-brand-blue inline-flex items-center px-4 py-2 text-sm leading-6 font-medium rounded-md transition ease-in-out duration-150 space-x-2"
              >
                <span class="cursor-default">Changes Pushed</span>
              </div>
              <el-button
                v-else
                v-can-enable="'dunning:write'"
                type="primary"
                @click="handlePublish"
                :loading="publishLoading"
                :disabled="saveState !== SAVE_STATE.SAVED"
                id="flow-publish"
                ><span class="font-bold">{{ !published ? 'Activate Exclusion' : 'Push Changes' }}</span></el-button
              >
            </div>
          </div>
        </header>
        <main v-if="exclusion" v-loading="publishLoading || loadState === LOAD_STATE.LOADING">
          <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <section>
              <div class="container max-w-screen-xl mt-8 rounded-lg">
                <ExclusionEditor :exclusion="exclusion" @onEdited="handleEdited" />
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
    <div v-else>
      <!-- Empty state -->
      <div class="bg-brand-sand w-full h-20"></div>
      <div class="container max-w-screen-md pt-20 space-y-12" element-loading-background="rgba(255, 255, 255, 0.05)">
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="1.5" :primaryOpacity="0.9" :secondaryOpacity="0.9" />
        <content-loader
          primaryColor="#f8efe6"
          secondaryColor="#f8efe655"
          class="w-full"
          :speed="1.5"
          :primaryOpacity="0.65"
          :secondaryOpacity="0.65"
        />
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="2.5" :primaryOpacity="0.5" :secondaryOpacity="0.5" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ContentLoader } from 'vue-content-loader';
import * as mutations from '@/store/mutation-types';
import * as actions from '@/store/action-types';
import { SAVE_STATE, LOAD_STATE } from '@/store/shared';
import ExclusionEditor from '@/components/Dunning/ExclusionEditor.vue';
import BuilderTab from '@/components/UI/BuilderTab.vue';
import SaveStateTag from '@/components/UI/SaveStateTag.vue';
import eventBus from '@/helpers/event-bus';

export default {
  name: 'DunningExclusionBuilder',
  components: {
    SaveStateTag,
    ExclusionEditor,
    ContentLoader,
  },
  data() {
    return {
      SAVE_STATE,
      LOAD_STATE,
      loadState: LOAD_STATE.LOADING,
      publishLoading: false,
      createLoading: false,
      newExclusion: null,
    };
  },
  async created() {
    if (this.exclusionId === 'new') {
      this.newExclusion = {
        name: '',
        filters: [],
        enabled: true,
        saveState: SAVE_STATE.UNSAVED,
      };
    } else {
      await this.$store.dispatch(actions.SYNC_DUNNING);
    }
    this.loadState = LOAD_STATE.LOADED;
  },
  computed: {
    exclusions() {
      return this.$store.state.dunningExclusions || {};
    },
    exclusionId() {
      return this.$route.params.id;
    },
    exclusion() {
      if (this.isNew) {
        return this.newExclusion;
      }
      return this.exclusions[this.exclusionId];
    },
    saveState() {
      return this.exclusion?.saveState;
    },
    isNew() {
      return this.exclusionId === 'new';
    },
    isValid() {
      return !!this.exclusion?.filters.length && !!this.exclusion?.name.match(/\S/);
    },
    published() {
      return !!this.exclusion?.publishedAt;
    },
    hasUnpublishedChanges() {
      if (!this.exclusion) return false;
      const { publishedAt, updatedAt } = this.exclusion;
      return !publishedAt || new Date(updatedAt) - new Date(publishedAt) > 0;
    },
  },
  methods: {
    async handlePublish() {
      this.publishLoading = true;
      const { resultsByType } = await this.$store.dispatch(actions.PUBLISH_DUNNING_EXCLUSION, { id: this.exclusion._id });
      if (resultsByType) {
        // if started already, deactivated
        // if not started (e.g. no emails sent), deleted
        const { emailsDeactivated, emailsDeleted, campaignsDeleted, campaignsDeactivated } = resultsByType;
        const totalBlocked = campaignsDeleted + campaignsDeactivated;
        if (totalBlocked > 0) {
          this.$notify({
            title: 'Exclusion Published',
            message: `<ul class="list-disc space-y-1"><li>${totalBlocked} existing campaigns canceled</li></ul>`,
            type: 'success',
            duration: 5000,
            dangerouslyUseHTMLString: true,
          });
        } else {
          this.$notify({
            title: 'Exclusion Published',
            message: `Rules applied successfully`,
            type: 'success',
            duration: 5000,
          });
        }
      }
      this.publishLoading = false;
    },
    async create() {
      this.createLoading = true;
      const exclusion = await this.$store.dispatch(actions.CREATE_DUNNING_EXCLUSION, { data: this.exclusion });
      this.$router.push({ name: 'dunningexclusion', params: { id: exclusion._id } });
      this.createLoading = false;
    },
    handleEdited(updates) {
      if (this.isNew) {
        this.newExclusion = {
          ...this.newExclusion,
          ...updates,
        };
      } else {
        this.$store.commit(mutations.STORE_DUNNING_EXCLUSION_DATA, { id: this.exclusionId, data: { saveState: SAVE_STATE.UNSAVED, ...updates } });
        this.$store.dispatch(actions.SCHEDULE_SAVE_DUNNING_EXCLUSION, { id: this.exclusionId });
      }
    },
  },
};
</script>
